import { getConfig } from "../config";

/** Shown instead of a version when the Key Vault value never arrived (see getAppVersion). */
export const UNKNOWN_APP_VERSION = "dev";

/** The unreplaced injection token, i.e. "nobody stamped a version into this build". */
const UNREPLACED_TOKEN = "__APP_VERSION__";

/**
 * The application version to display (the header's info tooltip).
 *
 * Sourced from the Key Vault secret `AppVersion`, which flows into
 * `window.config.appVersion` by the same path as the Syncfusion key: `config.aspx`
 * (server env var `APP_VERSION`) when deployed somewhere that executes .aspx, or the
 * `__APP_VERSION__` token the pipeline injects into index.html at build time (the real
 * path on Azure Static Web Apps). Each environment's vault holds its own value, so a
 * release is re-stamped by editing the secret — no code change, no rebuild of source.
 *
 * Returns `UNKNOWN_APP_VERSION` when the value is absent, blank, or still the unreplaced
 * token (local dev). That is deliberate: a hardcoded fallback version would be silently
 * wrong forever, whereas "dev" is visibly not a release. Read lazily via getConfig() so it
 * reflects the live window.config and stays mockable in tests.
 */
export const getAppVersion = (): string => {
  const raw = (getConfig()?.appVersion ?? "").trim();
  // Anything that isn't a real version falls back: blank, the unreplaced build token, or an
  // unreplaced Azure Pipelines macro such as "$(AppVersion)" (a vault without the secret).
  // No version starts with "$", so that prefix check needs no knowledge of the macro's name.
  const stamped = raw !== "" && raw !== UNREPLACED_TOKEN && !raw.startsWith("$");
  return stamped ? raw : UNKNOWN_APP_VERSION;
};

/**
 * The full label the headers render, e.g. "CSM 26.7.1.3". Kept here so both headers
 * (CSMShopfloorHeader and the TOS page's copy) can never drift apart in format.
 */
export const getAppVersionLabel = (): string => `CSM ${getAppVersion()}`;
